/**
 * Safe area inset management for iOS and Android Capacitor apps.
 *
 * Sets CSS custom properties on `:root` so all components can use them:
 *   --status-bar-height   (top inset — status bar, notch, Dynamic Island)
 *   --safe-area-bottom    (bottom inset — home indicator on iPhone X+)
 *   --safe-area-left      (left inset — iPad landscape, Android gesture nav)
 *   --safe-area-right     (right inset — same)
 *
 * Requires:
 *   - `capacitor-plugin-safe-area` (npm)
 *   - `viewport-fit=cover` in the viewport meta tag
 *
 * Improvements over v1:
 *   - Sets ALL four insets (v1 only set top)
 *   - Listens for runtime changes (rotation, split-screen) via SafeArea plugin events
 *   - Exported as pure functions — no React dependency, can be called from anywhere
 */

import { Capacitor } from "@capacitor/core"
import { SafeArea } from "capacitor-plugin-safe-area"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SafeAreaInsets {
  top: number
  bottom: number
  left: number
  right: number
}

// In-memory cache of the last values (for components that need numeric access)
let currentInsets: SafeAreaInsets = { top: 0, bottom: 0, left: 0, right: 0 }

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Read the current safe area insets from the native plugin and apply them
 * as CSS custom properties on `<html>`.
 *
 * Safe to call multiple times — idempotent.
 * On web (non-native), this is a no-op (CSS variables stay at 0px defaults).
 */
export async function initSafeAreaInsets(): Promise<SafeAreaInsets> {
  if (!Capacitor.isNativePlatform()) {
    return currentInsets
  }

  try {
    const { insets } = await SafeArea.getSafeAreaInsets()

    const top = insets?.top ?? 0
    const bottom = insets?.bottom ?? 0
    const left = insets?.left ?? 0
    const right = insets?.right ?? 0

    currentInsets = { top, bottom, left, right }
    applyCssVariables(currentInsets)

    console.log(
      `[SafeArea] insets: top=${top} bottom=${bottom} left=${left} right=${right}`,
    )
  } catch (error) {
    console.error("[SafeArea] Failed to get safe area insets:", error)
  }

  return currentInsets
}

/**
 * Subscribe to safe area changes (device rotation, split-screen, etc.).
 * Returns a cleanup function to remove the listener.
 *
 * On web this is a no-op that returns a noop cleanup.
 */
export async function watchSafeAreaChanges(): Promise<() => void> {
  if (!Capacitor.isNativePlatform()) {
    return () => {}
  }

  try {
    // capacitor-plugin-safe-area v5 supports addListener("safeAreaChanged", ...)
    const handle = await SafeArea.addListener(
      "safeAreaChanged",
      (data: { insets: SafeAreaInsets }) => {
        const { top, bottom, left, right } = data.insets
        currentInsets = { top, bottom, left, right }
        applyCssVariables(currentInsets)
        console.log(
          `[SafeArea] changed: top=${top} bottom=${bottom} left=${left} right=${right}`,
        )
      },
    )
    return () => {
      handle.remove()
    }
  } catch (error) {
    // Plugin might not support the listener — fall back to manual refresh
    console.warn("[SafeArea] addListener not supported, using poll on resume only:", error)
    return () => {}
  }
}

/**
 * Get the current cached safe area insets (synchronous).
 * Useful for components that need numeric values, not just CSS.
 */
export function getSafeAreaInsets(): SafeAreaInsets {
  return { ...currentInsets }
}

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

function applyCssVariables(insets: SafeAreaInsets) {
  const root = document.documentElement.style
  root.setProperty("--status-bar-height", `${insets.top}px`)
  root.setProperty("--safe-area-bottom", `${insets.bottom}px`)
  root.setProperty("--safe-area-left", `${insets.left}px`)
  root.setProperty("--safe-area-right", `${insets.right}px`)
}
